import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useSSRContext } from "../../context/SSRContext";
import { useJourneyDynamicDetails } from "../../../../context";
import { SSROptionsType } from "src/Modules/Flights/constants/JourneyDetails";

const isSSRItemSelected = (ssrItem?: Record<string, any>) =>
  !!ssrItem && Object.values(ssrItem).some((value) => value && Object.keys(value).length !== 0);

const SSRSummary: FC<{ stepButtons: ReactNode }> = ({ stepButtons }) => {
  const { t } = useTranslation();
  const { SSROptions, flightOptions } = useSSRContext();
  const { passengersState } = useJourneyDynamicDetails();
  const [passengers] = passengersState;

  const getSSRSummary = (option: NonNullable<SSROptionsType>) => {
    const units =
      option.ssrSelectionType === "segment"
        ? flightOptions.filter((flight, index) => flightOptions.findIndex((f) => f.segmentIndex === flight.segmentIndex) === index)
        : flightOptions;

    const ssrItems = passengers.passengers.flatMap((passenger) =>
      units.map((unit) => {
        const segment = passenger?.segments[unit.segmentIndex];
        if (option.ssrSelectionType === "segment") return segment?.ssr?.[option.value];
        return segment?.flights[unit.flightIndex]?.ssr?.[option.value];
      }),
    );

    return { count: ssrItems.filter(isSSRItemSelected).length, total: ssrItems.length };
  };

  return (
    <div className="bg-background-default flex w-full items-center justify-between gap-4 rounded-md px-6 outline outline-border-subtle">
      <div className="flex flex-wrap items-center gap-3 py-4">
        <span className="text-font-minimal text-sm">{t("Flights.details.ssr.yourExtras")}</span>
        {SSROptions.map((option) => {
          if (!option) return null;
          const { count, total } = getSSRSummary(option);
          const isAdded = count > 0;
          return (
            <span
              key={option.value}
              className={classNames(
                "flex items-center gap-1 rounded-full border px-3 py-1 text-xs",
                isAdded
                  ? "border-border-active bg-background-primary-lighter text-font-active"
                  : "border-border-warning bg-background-warning text-font-warning border-dashed",
              )}
            >
              <span className="font-semibold">{option.label}</span>
              <span>{isAdded ? t("Flights.details.ssr.summarySelected", { count, total }) : t("Flights.details.ssr.notAdded")}</span>
            </span>
          );
        })}
      </div>
      <div className="shrink-0">{stepButtons}</div>
    </div>
  );
};

export default SSRSummary;
